import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useState, useMemo, useCallback } from "react";
import { IndexCellWrapper } from "../Styles.js";
import { useBulkRowSelection } from "../../../context/useBulkRowSelection.js";
import { useDataBufferContext } from "../../../context/useDataBuffer.js";
import { SelectedIndexCellStyle, getSelectedCellRange } from "../../../hooks/useSelectCells.js";
import { named } from "../../../utils/named.js";
import { rowHeaderTooltipId } from "../../../utils/utils.js";
import { Spinner, CheckboxInput } from "@flatfile/design-system";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { IndexColumnCellInner, CheckboxWrapper, StyledLabel } from "./Styles.js";
import RowDividerActionsHandle from "./RowDividerActionsHandle.js";
import { useRowDividerActions } from "../../../hooks/useRowDividerActions.js";
const getSelectionState = (isFirstRow, rowSelectionType) => {
  return rowSelectionType === "bulk" || rowSelectionType === "single" && !isFirstRow;
};
const RowHeader = ({
  rowData,
  rowIndex,
  isSelected,
  isFirstRow,
  isGhostRow,
  onSelectedRows,
  onClick,
  onMouseDown,
  onMouseUp,
  onMouseEnter,
  tableId,
  totalRowCount,
  rowSelectionType,
  clearCellSelection,
  decorateCell,
  canInsert,
  ...props
}) => {
  const DecorateCellComponent = decorateCell;
  const { buffer } = useDataBufferContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const cellRef = useRef(null);
  const dividerActionsHandleRef = useRef(null);
  const selectionEnabled = getSelectionState(isFirstRow, rowSelectionType);
  const [isHovered, setIsHovered] = useState(false);
  const [rowSelectionFailed, setRowSelectionFailed] = useState(false);
  const [loadingSelection, setLoadingSelection] = useState(false);
  const {
    toggleAllRows,
    toggleSingleRow,
    toggleUntil,
    rowIsSelected,
    tableSelectionStatus
  } = useBulkRowSelection();
  const { onAddRow } = useRowDividerActions(rowIndex, rowData == null ? void 0 : rowData.id);
  const isRowSelected = useMemo(() => {
    if (isFirstRow) {
      return tableSelectionStatus && tableSelectionStatus() === "all";
    }
    if (!rowData)
      return false;
    return isSelected || rowIsSelected && rowIsSelected(rowData);
  }, [isSelected, isFirstRow, rowIsSelected, rowData, tableSelectionStatus]);
  const isPartiallySelected = useMemo(() => {
    return tableSelectionStatus && tableSelectionStatus() === "some";
  }, [tableSelectionStatus]);
  const styles = useMemo(() => {
    if (!isRowSelected || isFirstRow)
      return props.style;
    return { ...props.style, ...SelectedIndexCellStyle(props.theme) };
  }, [isRowSelected, isFirstRow, props.style]);
  const label = useMemo(() => {
    if (isGhostRow || isFirstRow)
      return "";
    return (rowData == null ? void 0 : rowData.label) ?? rowIndex;
  }, [rowIndex, rowData, isGhostRow, isFirstRow]);
  const tooltipText = useMemo(() => {
    if (rowSelectionFailed) {
      return t("sheet.table.indexRow.tooltip.rowSelectionFailed");
    }
    if (isFirstRow) {
      if (!totalRowCount && selectionEnabled) {
        return t("sheet.table.indexRow.tooltip.noData");
      }
    } else if (isGhostRow) {
      return t("sheet.table.indexRow.tooltip.createRow");
    } else if (isRowSelected || isPartiallySelected) {
      return t("sheet.table.indexRow.tooltip.rowNumber", { number: rowIndex });
    }
    return "";
  }, [
    isGhostRow,
    isFirstRow,
    isRowSelected,
    isPartiallySelected,
    totalRowCount,
    selectionEnabled,
    rowSelectionFailed
  ]);
  const handleCheckboxChange = useCallback(
    async (event, shiftKey = false) => {
      if (!selectionEnabled)
        return;
      clearCellSelection();
      if (isFirstRow) {
        toggleAllRows();
      } else {
        if (shiftKey) {
          try {
            setLoadingSelection(true);
            await toggleUntil(totalRowCount, rowData, rowSelectionType);
          } catch (error) {
            setRowSelectionFailed(true);
            setTimeout(() => {
              setRowSelectionFailed(false);
            }, 5e3);
          } finally {
            setLoadingSelection(false);
          }
        } else {
          toggleSingleRow(totalRowCount, rowData, rowSelectionType);
        }
      }
    },
    [
      toggleAllRows,
      toggleSingleRow,
      rowData,
      totalRowCount,
      selectionEnabled,
      clearCellSelection
    ]
  );
  const handleMouseDown = useCallback(
    (event) => {
      if (selectionEnabled)
        return;
      onMouseDown(cellRef, event.button, event.shiftKey);
    },
    [cellRef, selectionEnabled]
  );
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (selectionEnabled)
      return;
    onMouseEnter(cellRef);
  }, [cellRef, selectionEnabled]);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);
  const handleMouseUp = useCallback(
    (event) => {
      if (selectionEnabled)
        return;
      event.preventDefault();
      event.stopPropagation();
      onMouseUp(cellRef);
      if (onSelectedRows) {
        const selectedCellRange = getSelectedCellRange({ tableId });
        const selectedRows = [];
        for (let i = selectedCellRange[0][0]; i <= selectedCellRange[1][0]; i++) {
          const position = i - 1;
          const row = buffer.getRow(position);
          if (row) {
            selectedRows.push(row);
          } else {
            selectedRows.push({ position, rowIndex: i, cells: [] });
          }
        }
        onSelectedRows(selectedRows);
      }
    },
    [cellRef, selectionEnabled, onSelectedRows]
  );
  const handleOnClick = useCallback(
    (event) => {
      if (onClick)
        onClick(event);
      handleCheckboxChange(event, event.shiftKey);
    },
    [onClick, handleCheckboxChange]
  );
  return /* @__PURE__ */ jsxs(
    IndexCellWrapper,
    {
      ...props,
      ref: cellRef,
      "data-cell-wrapper": "IndexCellWrapper",
      style: styles,
      isFirstRow,
      onClick: handleOnClick,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      hovered: isHovered,
      selected: isRowSelected,
      children: [
        /* @__PURE__ */ jsxs(
          IndexColumnCellInner,
          {
            "data-tooltip-content": tooltipText,
            "data-tooltip-id": rowHeaderTooltipId(tableId),
            children: [
              selectionEnabled && (!isGhostRow || isFirstRow) && /* @__PURE__ */ jsx(
                CheckboxWrapper,
                {
                  persist: isFirstRow || isRowSelected || isPartiallySelected,
                  tintBackground: isRowSelected && !isFirstRow,
                  className: "checkbox",
                  children: loadingSelection ? /* @__PURE__ */ jsx(Spinner, {}) : /* @__PURE__ */ jsx(
                    CheckboxInput,
                    {
                      id: `cell-${rowIndex}`,
                      disabled: !totalRowCount,
                      checked: isRowSelected,
                      partial: isFirstRow && isPartiallySelected,
                      onChange: () => {
                      },
                      borderColor: theme.inputs.checkbox.borderColor,
                      color: theme.inputs.checkbox.color
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx(StyledLabel, { hidden: isRowSelected, children: label })
            ]
          }
        ),
        canInsert && /* @__PURE__ */ jsx(
          RowDividerActionsHandle,
          {
            ref: dividerActionsHandleRef,
            onAddRow
          }
        ),
        DecorateCellComponent && (rowData == null ? void 0 : rowData.id) && /* @__PURE__ */ jsx(
          DecorateCellComponent,
          {
            id: rowData.id,
            isActive: false,
            isSelected: isRowSelected,
            isFocused: false,
            isHovered,
            isEditing: false,
            isGhostRow: isGhostRow ?? false,
            isBoolean: false,
            isHidden: false,
            isLastRow: false,
            isLastColumn: false,
            isFirstColumn: false,
            isFirstNonHeaderRow: false,
            isRowHeader: true
          }
        )
      ]
    }
  );
};
named("RowHeader", RowHeader);
export {
  RowHeader as default
};
